import { useEffect, useMemo, useState } from "react";
import { Alert, Card, Space, Spin, Typography, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import AuthShell from "components/auth/AuthShell";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { resolvePostLoginRedirect } from "utils/postLoginRedirect";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text, Title } = Typography;

const normalizeTokenParam = (value) => value || undefined;

const AuthLinkedInCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeSocialSession } = useAuth();
  const [error, setError] = useState("");
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    let cancelled = false;

    const completeLinkedInSignIn = async () => {
      const status = params.get("status");
      const callbackError = params.get("error");
      const token = params.get("token");
      const messageParam = params.get("message");
      const identityToken = params.get("identity_token");
      const providerUserId = params.get("provider_user_id");
      const userInfoParam = params.get("user_info");

      if (callbackError || status === "error") {
        throw new Error(callbackError || "LinkedIn sign-in failed.");
      }
      if (status === "link_required" || status === "requires_password") {
        let userInfo = {};
        if (userInfoParam) {
          try {
            userInfo = JSON.parse(userInfoParam);
          } catch (parseError) {
            console.warn("Unable to parse LinkedIn linking profile:", parseError);
          }
        }
        navigate(paths.socialSetup || paths.appleSetup, {
          replace: true,
          state: {
            mode: "link",
            provider: "linkedin",
            authPayload: {
              identityToken,
              idToken: identityToken,
              providerUserId,
              linkedinUserId: providerUserId,
            },
            providerUserId,
            userInfo,
            message:
              messageParam ||
              "An account already exists with this email. Confirm linking to continue with LinkedIn.",
            requiresPassword: status === "requires_password",
            requiresLinkApproval: true,
          },
        });
        return;
      }
      if (status !== "success" || !token) {
        throw new Error("LinkedIn did not return a session token.");
      }

      const result = await completeSocialSession({
        accessToken: token,
        refreshToken: normalizeTokenParam(params.get("refresh_token")),
        accessTokenExpiresAt: normalizeTokenParam(params.get("access_token_expires_at")),
        refreshTokenExpiresAt: normalizeTokenParam(params.get("refresh_token_expires_at")),
      });

      if (!result?.success) {
        throw new Error(result?.error || "LinkedIn sign-in failed.");
      }
      if (cancelled) return;

      message.success("Signed in with LinkedIn.");
      navigate(params.get("next") || resolvePostLoginRedirect(location, paths.pmDashboard), {
        replace: true,
      });
    };

    completeLinkedInSignIn().catch((err) => {
      if (cancelled) return;
      setError(err?.message || "LinkedIn sign-in failed.");
      message.error(err?.message || "LinkedIn sign-in failed.");
    });

    return () => {
      cancelled = true;
    };
  }, [completeSocialSession, location, navigate, params]);

  return (
    <AuthShell mode="center">
      <Card
        variant="borderless"
        style={{
          width: "min(100%, 460px)",
          borderRadius: 22,
          border: "1px solid #DDF3E8",
          boxShadow: "0 24px 60px rgba(0, 64, 56, 0.14)",
          background: OLIVEEB_COLORS.white,
          textAlign: "center",
        }}
      >
        <Space orientation="vertical" size={16} style={{ width: "100%" }}>
          <Text
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: OLIVEEB_COLORS.oliveebGreen,
              fontWeight: 900,
              fontSize: 12,
            }}
          >
            LinkedIn
          </Text>
          <Title level={3} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
            Completing sign in
          </Title>
          {error ? (
            <Alert type="error" showIcon message="LinkedIn sign-in failed" description={error} />
          ) : (
            <>
              <Spin />
              <Paragraph style={{ color: OLIVEEB_COLORS.textMuted, margin: 0 }}>
                We are finishing your secure Ovacus session.
              </Paragraph>
            </>
          )}
        </Space>
      </Card>
    </AuthShell>
  );
};

export default AuthLinkedInCallback;
